import { axios } from '/plugins/axios.client';
import { errorFromResponse } from '/src/common/general-utils';
import { i18n } from '/plugins/i18n.client';
const errorHandler = errorFromResponse(i18n.global.t.bind(i18n));
import { toast } from '/plugins/toaster.client.js';
import { widgetForState } from '/src/features/widgets/edit/widget-helper';

/**
 * Fetch details of dashboard by UUID
 * @param {String} UUID UUID of the dashboard whose details are to be fetched
 */
export const getDashboard = async (UUID, fromCockpit = false, isEmbedded = false) => {
  let dashboard = {};
  try {
    dashboard = await axios.$get(`/api/pages/${UUID}/byRef`, {
      headers: { 'from-cockpit': fromCockpit, 'is-embedded': isEmbedded }
    });
  } catch (error) {
    toast.error(errorHandler('admin.dashboards.manage.retrieve_action', error));
    // Setting flag as it is being used at many places and at each places we need some different handling
    // For design it will be redirect to un auth from dashboard-design/action
    // For cockpit and hierarchy menu will show alert in its widgets
    // For display it will be handled in dashboard-display component
    if (error && error.response && error.response.status === 401) {
      dashboard.isUnauthorized = true;
    }
  }
  if (dashboard.LayoutV2) {
    try {
      dashboard.LayoutV2 = JSON.parse(dashboard.LayoutV2);
      dashboard.LayoutV2 = dashboard.LayoutV2.map((s) => ({
        ...s,
        i: s.i?.UUID || s.i,
        type: s.i?.type || s.type
      }));
    } catch (error) {
      toast.error(i18n.global.t('admin.dashboards.design.invalid_layout'));
      dashboard.LayoutV2 = [];
    }
  } else {
    dashboard.LayoutV2 = [];
  }
  if (dashboard.PreloadFilters) {
    try {
      const preloadFilters = JSON.parse(dashboard.PreloadFilters);
      dashboard.PreloadFilters = preloadFilters.filter((filter) => filter.field && filter.field !== 'null');
    } catch (err) {
      dashboard.PreloadFilters = [];
    }
  }
  return dashboard;
};

/**
 * Fetch details of widgets by dashboard UUID
 * @param {String} UUID UUID of the dashboard whose widgets are to be fetched
 */
export const getWidgets = async (dashboardUUID, fromCockpit = false, isEmbedded = false) => {
  try {
    return (
      await axios.$get(`/api/widgets/loadWidgets?pageUUID=${dashboardUUID}`, {
        headers: { 'from-cockpit': fromCockpit, 'is-embedded': isEmbedded }
      })
    ).map((widget) => widgetForState(widget));
  } catch (error) {
    toast.error(errorHandler('admin.dashboards.design.retrieve_widget_action', error));
    return [];
  }
};

/**
 * Get configured dashboard and widgets for given dashboard uuid. It will return dashboard and widgetMapByUUId
 * @param {String} UUID UUID of the dashboard whose dashboard and widgets to be fetched
 */
export const getDashboardAndWidgets = async (
  dashboardUUID,
  filterEmptyWidgets = true,
  fromCockpit = false,
  isEmbedded = false
) => {
  const [dashboard, widgets] = await Promise.all([
    getDashboard(dashboardUUID, fromCockpit, isEmbedded),
    getWidgets(dashboardUUID, fromCockpit, isEmbedded)
  ]);

  let layoutWidgetsMap = {};
  let widgetsMap = widgets.reduce((widgetsMapByUuid, widget) => {
    widgetsMapByUuid[widget.UUID] = { ...widget, dashboardURL: dashboard.URL, dashboardTitle: dashboard.title };
    if (widget.type === 'Load Widget' && widget.EmbedUUID && !widgetsMapByUuid[widget.EmbedUUID]) {
      widgetsMapByUuid[widget.EmbedUUID] = {
        ...(widgetForState(widget.EmbedWidget) || {}),
        dashboardURL: dashboard.URL
      };
    }
    return widgetsMapByUuid;
  }, {});
  if (filterEmptyWidgets) {
    const widgetUuids = Object.keys(widgetsMap);
    dashboard.LayoutV2 = dashboard.LayoutV2.filter((widget) => widgetUuids.includes(widget.i));
  } else {
    //for design page we will need empty widgets in layout json as it may have some widgets that are still not configured
    layoutWidgetsMap = dashboard.LayoutV2.map((layout) => ({ UUID: layout.i, type: layout.type })).reduce(
      (widgetsMapByUUID, widget) => {
        widgetsMapByUUID[widget.UUID] = { ...widget };
        return widgetsMapByUUID;
      },
      {}
    );
  }
  const widgetsByUuid = {
    ...layoutWidgetsMap,
    ...widgetsMap
  };

  return { dashboard, widgetsByUuid };
};

export const getSocketEndpoint = async () => {
  try {
    return await axios.$get('/api/pages/socket/client-endpoint');
  } catch (err) {
    toast.error('Unable to fetch socket Endpoint.');
  }
};
